import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Banknote, CheckCircle2, XCircle, Send, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { useAllPayouts, useUpdatePayout, useSendPayoutToBank } from "@/hooks/platform/usePayouts";
import { LoadingState, ErrorState, EmptyState } from "@/components/dashboard/common/PageShell";

const statusStyle: Record<string, string> = {
  pending: "bg-amber-600",
  approved: "bg-blue-600",
  paid: "bg-emerald-600",
  rejected: "bg-red-600",
  cancelled: "bg-gray-700",
};

const PayoutsAdmin = () => {
  const { data: payouts = [], isLoading, error, refetch } = useAllPayouts();
  const update = useUpdatePayout();
  const sendToBank = useSendPayoutToBank();
  const [batching, setBatching] = useState(false);
  const [refs, setRefs] = useState<Record<string, string>>({});

  const set = (id: string, patch: Record<string, unknown>, msg: string) =>
    update.mutate({ id, ...patch } as never, {
      onSuccess: () => toast.success(msg),
      onError: (e) => toast.error(e instanceof Error ? e.message : "Update failed"),
    });

  const pendingTotal = payouts.filter((p) => p.status === "pending").reduce((s, p) => s + Number(p.amount), 0);
  const paidTotal = payouts.filter((p) => p.status === "paid").reduce((s, p) => s + Number(p.amount), 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white">Creator Payouts</h2>
          <p className="text-sm text-gray-400">Approved withdrawals are sent straight to members' bank accounts through Paystack and marked paid when the bank confirms.</p>
        </div>
        <Button
          disabled={batching || !payouts.some((p) => p.status === "approved" && !p.transfer_reference)}
          onClick={async () => {
            const queue = payouts.filter((p) => p.status === "approved" && !p.transfer_reference);
            setBatching(true);
            let sent = 0, failed = 0;
            for (const p of queue) {
              try { await sendToBank.mutateAsync(p.id); sent++; } catch { failed++; }
            }
            setBatching(false);
            toast[failed ? "error" : "success"](`${sent} payout${sent === 1 ? "" : "s"} sent to bank${failed ? `, ${failed} failed` : ""}`);
          }}
          className="gap-1"
        >
          {batching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send all approved to bank
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        {[
          { label: "Requests", value: payouts.length.toString() },
          { label: "Awaiting approval", value: `R${pendingTotal.toFixed(0)}` },
          { label: "Paid out", value: `R${paidTotal.toFixed(0)}` },
        ].map((s) => (
          <Card key={s.label} className="bg-gray-900/60 border-gray-800">
            <CardHeader className="pb-2"><CardTitle className="text-sm text-gray-400">{s.label}</CardTitle></CardHeader>
            <CardContent><p className="text-3xl font-bold text-white">{s.value}</p></CardContent>
          </Card>
        ))}
      </div>

      {isLoading ? (
        <LoadingState label="Loading payout requests…" />
      ) : error ? (
        <ErrorState message={(error as Error).message} onRetry={() => refetch()} />
      ) : payouts.length === 0 ? (
        <EmptyState icon={Banknote} title="No payout requests" description="Withdrawal requests from creators appear here." />
      ) : (
        <div className="space-y-3">
          {payouts.map((p) => (
            <Card key={p.id} className="bg-gray-900/60 border-gray-800">
              <CardContent className="p-4 flex flex-wrap items-center gap-4">
                <div className="flex-1 min-w-[240px]">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-white">R{Number(p.amount).toFixed(0)}</p>
                    <Badge className={`${statusStyle[p.status] ?? "bg-gray-700"} border-0 capitalize`}>{p.status}</Badge>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {p.bank_name || "Bank"} · {p.account_holder || "—"} · ••••{(p.account_number || "").slice(-4)}
                    {p.branch_code ? ` · Branch ${p.branch_code}` : ""} · {format(new Date(p.created_at), "d MMM yyyy")}
                  </p>
                  {p.note && <p className="text-xs text-gray-400 mt-1">“{p.note}”</p>}
                </div>

                {["pending", "approved"].includes(p.status) && (
                  <div className="flex items-center gap-2">
                    {p.status === "pending" && (
                      <>
                        <Button
                          size="sm"
                          className="gap-1"
                          disabled={update.isPending || sendToBank.isPending}
                          onClick={async () => {
                            try {
                              await update.mutateAsync({ id: p.id, status: "approved" } as never);
                              const res = await sendToBank.mutateAsync(p.id);
                              toast.success(res?.status === "success" ? `R${Number(p.amount).toFixed(0)} sent to ${p.account_holder || "the member"}'s bank` : `Approved — transfer ${res?.status ?? "queued"} at Paystack`);
                            } catch (e) {
                              toast.error(e instanceof Error ? `Approved, but the transfer failed: ${e.message}` : "Transfer failed");
                            }
                          }}
                        >
                          {sendToBank.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Approve & pay
                        </Button>
                        <Button size="sm" variant="outline" className="border-gray-700 gap-1" onClick={() => set(p.id, { status: "approved" }, "Approved")}>
                          <CheckCircle2 className="w-3 h-3" /> Approve only
                        </Button>
                        <Button size="sm" variant="outline" className="border-gray-700 gap-1 text-red-400" onClick={() => set(p.id, { status: "rejected" }, "Rejected")}>
                          <XCircle className="w-3 h-3" /> Reject
                        </Button>
                      </>
                    )}
                    {p.status === "approved" && (
                      <>
                        <Input
                          value={refs[p.id] ?? ""}
                          onChange={(e) => setRefs({ ...refs, [p.id]: e.target.value })}
                          placeholder="Bank transfer reference"
                          className="w-52 bg-gray-950 border-gray-800 text-white"
                        />
                        <Button
                          size="sm"
                          className="gap-1"
                          disabled={sendToBank.isPending}
                          onClick={() =>
                            sendToBank.mutate(p.id, {
                              onSuccess: (res) =>
                                toast.success(
                                  res?.status === "success"
                                    ? "Transfer sent to the bank"
                                    : `Transfer ${res?.status ?? "queued"} at Paystack`,
                                ),
                              onError: (e) => toast.error(e instanceof Error ? e.message : "Transfer failed"),
                            })
                          }
                        >
                          {sendToBank.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Send to bank
                        </Button>
                        <Button size="sm" variant="outline" className="border-gray-700 gap-1" onClick={() => set(p.id, { status: "paid", transfer_reference: refs[p.id] || null }, "Marked as paid")}>
                          Mark paid manually
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default PayoutsAdmin;
